import React from "react";
import { v4 as randomUUID } from "uuid";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PatientBillInfoDto } from "../../../../api-client";
import { useLocationId } from "../../../../stores/User/User.hooks";
import { useCreateSepaDirectDebit } from "../../../../stores/Sepa/sepa.mutations";
import { maskIbanForDisplay } from "../../../../stores/Sepa/sepa.validation";
import { WaitingBackdrop } from "../../../Transactions/Base/components/BackDrop";
import { RcurMandateOption } from "../hooks/useActiveRcurMandates";
import { billChargeText, billTAccountId } from "./sepaBill";

const BUTTON_CLASS =
  "h-7 rounded-lg border-0 bg-gradient-to-b from-[#5aa6c4] to-[#4189a8] px-2.5 text-sm font-medium text-white shadow-[0_1px_2px_rgba(65,137,168,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-all duration-200 hover:from-[#4f9cbb] hover:to-[#3a7f9c] active:scale-[0.97]";

const fmtDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString("de-DE") : "";

interface Props {
  bill: PatientBillInfoDto;
  patientId: number;
  /** ACTIVE recurring (RCUR) mandates matching the bill's account. */
  mandates: RcurMandateOption[];
  disabled?: boolean;
}

/**
 * "per Lastschrift" — charges the bill's due amount against an existing recurring
 * mandate. One mandate → charge directly; several → pick from a menu.
 */
const PerLastschriftButton: React.FC<Props> = ({ bill, patientId, mandates, disabled }) => {
  const locationId = useLocationId();
  const directDebit = useCreateSepaDirectDebit();

  const charge = (mandate: RcurMandateOption) => {
    directDebit.mutate({
      patientId,
      accountId: bill.accountId,
      mandateId: mandate.id,
      amount: Number(bill.dueAmount),
      text: `${billChargeText(bill)} - Patient: ${patientId}`,
      rxId: bill.rxId,
      tAccountId: billTAccountId(bill),
      businessId: bill.businessId,
      locationId,
      // One key per click = one intent. React Query's automatic retries reuse this
      // same dto (same key), so a flaky network can't create a duplicate debit.
      idempotencyKey: randomUUID(),
    });
  };

  const isDisabled = disabled || directDebit.isPending || mandates.length === 0;

  if (mandates.length <= 1) {
    return (
      <>
        <WaitingBackdrop
          isWorking={directDebit.isPending}
          message="SEPA-Lastschrift wird verarbeitet..."
        />
        <Button
          className={BUTTON_CLASS}
          size="sm"
          disabled={isDisabled}
          onClick={() => charge(mandates[0])}
        >
          per Lastschrift
        </Button>
      </>
    );
  }

  return (
    <>
      <WaitingBackdrop
        isWorking={directDebit.isPending}
        message="SEPA-Lastschrift wird verarbeitet..."
      />
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button className={BUTTON_CLASS} size="sm" disabled={isDisabled}>
            per Lastschrift
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          {mandates.map((mandate) => (
            <DropdownMenuItem
              key={mandate.id}
              onClick={() => charge(mandate)}
              className="flex flex-col items-start gap-0.5"
            >
              <span className="text-sm font-medium text-slate-800">
                {mandate.bankHolderFirstName} {mandate.bankHolderLastName} ·{" "}
                <span className="font-mono">{maskIbanForDisplay(mandate.bankIban)}</span>
              </span>
              <span className="text-xs text-muted-foreground">
                {mandate.mandateReference ?? `Mandat ${mandate.id.slice(0, 8)}…`}
                {mandate.signedDate ? ` · signiert ${fmtDate(mandate.signedDate)}` : ""}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default PerLastschriftButton;
